import { Pipe, PipeTransform } from '@angular/core';
import { SettingsService } from '@app/shared/services/api/settings.service';
import {
  DEFAULT_TOOTH_NUMBERING_SYSTEM,
  TOOTH_NUMBERING_FDI,
  TOOTH_NUMBERING_PALMER,
  TOOTH_NUMBERING_UNIVERSAL
} from '@app/shared/constants/settings.constants';

/** Maps FDI tooth numbers to Universal or Palmer notation based on account setting. */
const FDI_TO_UNIVERSAL: Record<number, number | string> = {
  18: 1, 17: 2, 16: 3, 15: 4, 14: 5, 13: 6, 12: 7, 11: 8,
  21: 9, 22: 10, 23: 11, 24: 12, 25: 13, 26: 14, 27: 15, 28: 16,
  38: 17, 37: 18, 36: 19, 35: 20, 34: 21, 33: 22, 32: 23, 31: 24,
  41: 25, 42: 26, 43: 27, 44: 28, 45: 29, 46: 30, 47: 31, 48: 32,
  55: 'A', 54: 'B', 53: 'C', 52: 'D', 51: 'E',
  61: 'F', 62: 'G', 63: 'H', 64: 'I', 65: 'J',
  75: 'K', 74: 'L', 73: 'M', 72: 'N', 71: 'O',
  81: 'P', 82: 'Q', 83: 'R', 84: 'S', 85: 'T'
};

/** Palmer quadrant symbols: upper-right, upper-left, lower-left, lower-right */
const PALMER_SYMBOLS: Record<number, string> = {
  1: '\u2518', 2: '\u2514', 3: '\u250C', 4: '\u2510',
  5: '\u2518', 6: '\u2514', 7: '\u250C', 8: '\u2510'
};

@Pipe({
  name: 'toothNumber',
  pure: false
})
export class ToothNumberPipe implements PipeTransform {

  constructor(private settings: SettingsService) {}

  /**
   * Transforms FDI tooth number to display label based on account setting.
   * @param value FDI tooth number (e.g. 11, 18, 32)
   * @param system Override: one of the TOOTH_NUMBERING_* constants
   */
  transform(value: number | string, system?: string): string {
    if (value == null || value === '') {
      return '';
    }
    const fdi = typeof value === 'string' ? parseInt(value, 10) : value;
    if (isNaN(fdi)) {
      return String(value);
    }
    const sys = system || this.settings.getToothNumberingPreference() || DEFAULT_TOOTH_NUMBERING_SYSTEM;
    if (sys === TOOTH_NUMBERING_FDI) {
      return String(fdi);
    }
    if (sys === TOOTH_NUMBERING_UNIVERSAL) {
      const u = FDI_TO_UNIVERSAL[fdi];
      return u !== undefined ? String(u) : String(fdi);
    }
    if (sys === TOOTH_NUMBERING_PALMER) {
      return this.toPalmer(fdi);
    }
    return String(fdi);
  }

  private toPalmer(fdi: number): string {
    const quadrant = Math.floor(fdi / 10);
    const position = fdi % 10;
    const symbol = PALMER_SYMBOLS[quadrant] || '';
    const primaryLetters = ['', 'A', 'B', 'C', 'D', 'E'];
    const pos = quadrant >= 5 ? primaryLetters[position] : String(position);
    return pos + symbol;
  }
}
